"use client"
import type React from "react"
import { useState, useEffect } from "react"
import {
  Container,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Avatar,
  Alert,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tab,
  Tabs,
  alpha,
  IconButton,
} from "@mui/material"
import {
  Assignment as AssignmentIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  PriorityHigh as PriorityIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from "@mui/icons-material"
import { IonContent, IonPage, IonHeader, IonToolbar, IonTitle } from "@ionic/react"
import { getStaffAssignedRequest } from "../../../api/services/services"
import { getUserData } from "../../../common/utils"

interface AssignedService {
  assigned_services_id: string
  assigned_at: string
  note: string
  priority: "low" | "medium" | "high"
  service: {
    created_at: string
    project_id: string | null
    selected_items: { [key: string]: string[] }
    selected_services: string[]
    service_id: string
    service_type: string
    status: string
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

export const StaffRequestPage: React.FC = () => {
  // const userData = getUserData()
  // console.log({ userData })
  const [assignedServices, setAssignedServices] = useState<AssignedService[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedService, setSelectedService] = useState<AssignedService | null>(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
  const [tabValue, setTabValue] = useState(0)
  const [selectedProject, setSelectedProject] = useState(null)

  const [statusUpdate, setStatusUpdate] = useState("")
  const [progressNotes, setProgressNotes] = useState("")
  // const [currentUser] = useState({
  //   user_id: "staff_052632f51ff885e95cdd0aac6e5a3781",
  //   full_name: "EVELYN EYAGHWOBOR",
  //   email: "evelyne@company.com",
  // })
  const [projects, setProjects] = useState([])
  const [intakes, setIntakes] = useState([])

  useEffect(() => {
    const fetchAssignedServices = async () => {
      try {
        setLoading(true)
        const response = await getStaffAssignedRequest()
        console.log({ response })
        setAssignedServices(response.data)
        setProjects(response.data.projects || [])
        setIntakes(response.data.intakes || [])
      } catch (error) {
        console.error("Error fetching assigned services:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchAssignedServices()
  }, [])

  const handleViewService = (service: AssignedService) => {
    setSelectedService(service)
    setViewDialogOpen(true)
  }

  const handleUpdateService = (service: AssignedService) => {
    setSelectedService(service)
    setUpdateDialogOpen(true)
    setStatusUpdate("")
    setProgressNotes("")
  }

  const handleSubmitUpdate = async () => {
    try {
      console.log("Updating service:", {
        serviceId: selectedService?.assigned_services_id,
        status: statusUpdate,
        notes: progressNotes,
      })

      setUpdateDialogOpen(false)
      setSelectedService(null)
    } catch (error) {
      console.error("Error updating service:", error)
    }
  }

  const parseTags = (tagsString: string): string[] => {
    try {
      if (!tagsString) return []
      const cleanedString = tagsString.replace(/^"|"$/g, "").replace(/\\"/g, "")
      return cleanedString
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
    } catch (error) {
      return []
    }
  }

  const getPriorityColor = (priority: "low" | "medium" | "high") => {
    switch (priority) {
      case "high":
        return "error"
      case "medium":
        return "warning"
      case "low":
        return "success"
      default:
        return "default"
    }
  }

  const getServiceTypeIcon = (serviceType: string) => {
    switch (serviceType) {
      case "hr-outsourcing":
        return <PersonIcon />
      case "recruitment":
        return <BusinessIcon />
      default:
        return <AssignmentIcon />
    }
  }

  const handleViewProject = (project) => {
    setSelectedProject(project)
    console.log({ project })
    setViewDialogOpen(true)
  }

  const getProjectsByStatus = (status: string) => {
    return projects.filter((project) => project.status.toLowerCase() === status.toLowerCase()).length
  }

  const getIntakesByStatus = (status: string) => {
    return intakes.filter((intake) => {
      if (!intake.assigned_services || intake.assigned_services.length === 0) {
        return status === "pending"
      }
      return status === "assigned"
    }).length
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning"
      case "assigned":
        return "info"
      case "in-progress":
        return "primary"
      case "completed":
        return "success"
      case "rejected":
        return "error"
      default:
        return "default"
    }
  }

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>My Assignments</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>Loading your assignments...</Typography>
            </Box>
          </Container>
        </IonContent>
      </IonPage>
    )
  }

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonTitle>My Assignments</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
              My Service Assignments
            </Typography>
            <Typography variant="body1" color="text.secondary">
               Here are your current assignments.
            </Typography>
          </Box>

          {/* Stats Overview */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                bgcolor: "background.paper"
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                      Total Projects
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {projects.length}
                      </Typography>
                      <Avatar sx={{
                        bgcolor: alpha("#9c27b0", 0.1),
                        width: 40,
                        height: 40,
                        color: "#9c27b0"
                      }}>
                        <BusinessIcon />
                      </Avatar>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                bgcolor: "background.paper"
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                      Total Services
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {intakes.length}
                      </Typography>
                      <Avatar sx={{
                        bgcolor: alpha("#ed6c02", 0.1),
                        width: 40,
                        height: 40,
                        color: "#ed6c02"
                      }}>
                        <AssignmentIcon />
                      </Avatar>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                bgcolor: "background.paper"
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                      High Priority
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {projects.filter((s) => s.priority === "high").length}
                      </Typography>
                      <Avatar sx={{
                        bgcolor: alpha("#d32f2f", 0.1),
                        width: 40,
                        height: 40,
                        color: "#d32f2f"
                      }}>
                        <PriorityIcon />
                      </Avatar>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                bgcolor: "background.paper"
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                      Medium Priority
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {projects.filter((s) => s.priority === "medium").length}
                      </Typography>
                      <Avatar sx={{
                        bgcolor: alpha("#ed6c02", 0.1),
                        width: 40,
                        height: 40,
                        color: "#ed6c02"
                      }}>
                        <ScheduleIcon />
                      </Avatar>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                bgcolor: "background.paper"
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                      Low Priority
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {projects.filter((s) => s.priority === "low").length}
                      </Typography>
                      <Avatar sx={{
                        bgcolor: alpha("#2e7d32", 0.1),
                        width: 40,
                        height: 40,
                        color: "#2e7d32"
                      }}>
                        <CheckCircleIcon />
                      </Avatar>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{
            borderRadius: 2,
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
            mb: 3,
            bgcolor: "background.paper"
          }}>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  minHeight: 60
                }
              }}
            >
              <Tab label={`Recruitment Projects (${projects.length})`} />
              <Tab label={`HR Service Intakes (${intakes.length})`} />
            </Tabs>
          </Paper>

          {/* Assignments List */}
          <TabPanel value={tabValue} index={0}>
            {projects.length > 0 ? (
              <Grid container spacing={2}>
                {projects.map((service, index) => (
                  <Grid item xs={12} md={6} lg={4} key={service.assigned_services_id}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 2,
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                        transition: "all 0.2s ease",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
                        },
                      }}
                    >
                      <CardHeader
                        action={
                          <Chip
                            label={service.priority.toUpperCase()}
                            color={getPriorityColor(service.priority) as any}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              borderRadius: 1
                            }}
                          />
                        }
                        title={
                          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                            {service.projects.project_name.replace("-", " ").toUpperCase()}
                          </Typography>
                        }
                        subheader={
                          <Typography variant="body2" color="text.secondary">
                            Assigned: {new Date(service.assigned_at).toLocaleDateString()}
                          </Typography>
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        {service.note && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Assignment Notes:
                            </Typography>
                            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                              {service.note}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Client:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {service.projects.project_id || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Created:
                          </Typography>
                          <Typography variant="body2">
                            {service.projects.created_at
                              ? new Date(service.projects.created_at).toLocaleDateString()
                              : "N/A"}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ViewIcon />}
                            onClick={() => handleViewProject(service)}
                            sx={{
                              flex: 1,
                              borderRadius: 1,
                              textTransform: 'none'
                            }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Avatar sx={{
                  bgcolor: "background.default",
                  width: 80,
                  height: 80,
                  mx: "auto",
                  mb: 3
                }}>
                  <AssignmentIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}>
                  No project assignments yet
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "500px", mx: "auto", lineHeight: 1.6 }}>
                  You don't have any project assignments at the moment. Check back later or contact your administrator.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {intakes.length > 0 ? (
              <Grid container spacing={2}>
                {intakes.map((service, index) => (
                  <Grid item xs={12} md={6} lg={4} key={service.assigned_services_id}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 2,
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                        transition: "all 0.2s ease",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
                        },
                      }}
                    >
                      <CardHeader
                        avatar={
                          <Avatar sx={{
                            bgcolor: "background.default",
                            color: "text.primary"
                          }}>
                            {getServiceTypeIcon(service.service.service_type)}
                          </Avatar>
                        }
                        action={
                          <Chip
                            label={service.priority.toUpperCase()}
                            color={getPriorityColor(service.priority) as any}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              borderRadius: 1
                            }}
                          />
                        }
                        title={
                          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                            {service.service.service_type.replace("-", " ").toUpperCase()}
                          </Typography>
                        }
                        subheader={
                          <Typography variant="body2" color="text.secondary">
                            {/* ID: {service.service.service_id} */}
                          </Typography>
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Selected Services:
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {service.service.selected_services.map((selectedService, serviceIndex) => (
                              <Chip
                                key={serviceIndex}
                                label={selectedService.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: "0.7rem",
                                  borderRadius: 0.5,
                                  bgcolor: "background.default"
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        {service.note && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Assignment Notes:
                            </Typography>
                            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                              "{service.note}"
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Client:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {service.service.project_id || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Created:
                          </Typography>
                          <Typography variant="body2">
                            {service.service.created_at
                              ? new Date(service.service.created_at).toLocaleDateString()
                              : "N/A"}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ViewIcon />}
                            onClick={() => handleViewService(service)}
                            sx={{
                              flex: 1,
                              borderRadius: 1,
                              textTransform: 'none'
                            }}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleUpdateService(service)}
                            sx={{
                              flex: 1,
                              borderRadius: 1,
                              textTransform: 'none',
                              bgcolor: "text.primary",
                              "&:hover": { bgcolor: "text.secondary" },
                            }}
                          >
                            Update
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Avatar sx={{
                  bgcolor: "background.default",
                  width: 80,
                  height: 80,
                  mx: "auto",
                  mb: 3
                }}>
                  <AssignmentIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}>
                  No service assignments yet
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "500px", mx: "auto", lineHeight: 1.6 }}>
                  You don't have any service assignments at the moment. Check back later or contact your administrator.
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* View Service Dialog */}
          <Dialog
            open={viewDialogOpen}
            onClose={() => setViewDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2
              }
            }}
          >
            <DialogTitle sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedProject ? "Project" : "Service"} Assignment Details
              </Typography>
              <IconButton onClick={() => {
                setSelectedProject(null)
                setSelectedService(null)
                setViewDialogOpen(false)
              }} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {selectedProject && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Project Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Project ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                          {selectedProject.project_id || `#${selectedProject.projects.project_name}`}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Project Name
                        </Typography>
                        <Typography variant="body1">{selectedProject.projects.project_name}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={selectedProject.projects.status?.replace("_", " ").toUpperCase() ?? "PENDING"}
                          color={getStatusColor(selectedProject.projects.status?.toLowerCase() ?? "PENDING") as any}
                          sx={{ mt: 0.5, borderRadius: 1 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Created Date
                        </Typography>
                        <Typography variant="body1">
                          {new Date(selectedProject.projects.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Project Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Budget Range
                            </Typography>
                            <Typography variant="body1">{selectedProject.projects.budget_cost}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Tags
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                              {parseTags(selectedProject.projects.tags).map((tag, index) => (
                                <Chip
                                  key={index}
                                  label={tag}
                                  variant="outlined"
                                  size="small"
                                  sx={{ borderRadius: 0.5 }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Notes
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {selectedProject.note}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              )}


              {selectedService && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Assignment Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Assignment ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                          {selectedService.assigned_services_id}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Service ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                          {selectedService.service.service_id}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Priority
                        </Typography>
                        <Chip
                          label={selectedService.priority.toUpperCase()}
                          color={getPriorityColor(selectedService.priority) as any}
                          sx={{ mt: 0.5, borderRadius: 1 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Assignment Notes
                        </Typography>
                        <Typography variant="body1">{selectedService.note}</Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Service Details
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Service Type
                        </Typography>
                        <Chip
                          label={selectedService.service.service_type.replace("-", " ").toUpperCase()}
                          variant="outlined"
                          sx={{ mt: 0.5, borderRadius: 1 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Client
                        </Typography>
                        <Typography variant="body1">{selectedService.service.project_id || "N/A"}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Created Date
                        </Typography>
                        <Typography variant="body1">
                          {selectedService.service.created_at
                            ? new Date(selectedService.service.created_at).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Selected Services & Items
                      </Typography>
                      {selectedService.service.selected_items &&
                        Object.entries(selectedService.service.selected_items).map(([serviceKey, items]) => (
                          <Box key={serviceKey} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              {serviceKey.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}:
                            </Typography>
                            <List dense>
                              {items.map((item, itemIndex) => (
                                <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                                  <ListItemAvatar>
                                    <Avatar sx={{
                                      width: 24,
                                      height: 24,
                                      bgcolor: "background.default",
                                      color: "text.primary"
                                    }}>
                                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText primary={item} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        ))}
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => {
                  setViewDialogOpen(false); setSelectedProject(null)
                  setSelectedService(null)
                }}
                variant="outlined"
                sx={{ borderRadius: 1 }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Update Service Dialog */}
          <Dialog
            open={updateDialogOpen}
            onClose={() => setUpdateDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2
              }
            }}
          >
            <DialogTitle sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Update Service Progress
              </Typography>
              <IconButton onClick={() => setUpdateDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {selectedService && (
                <Box>
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Service:</strong> {selectedService.service.service_id}
                      <br />
                      <strong>Priority:</strong> {selectedService.priority.toUpperCase()}
                    </Typography>
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Status Update</InputLabel>
                        <Select
                          value={statusUpdate}
                          label="Status Update"
                          onChange={(e) => setStatusUpdate(e.target.value)}
                          sx={{ borderRadius: 1 }}
                        >
                          <MenuItem value="started">Started Working</MenuItem>
                          <MenuItem value="in-progress">In Progress</MenuItem>
                          <MenuItem value="review">Ready for Review</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="blocked">Blocked/Need Help</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Progress Notes"
                        placeholder="Add notes about your progress, any challenges, or updates for the client..."
                        value={progressNotes}
                        onChange={(e) => setProgressNotes(e.target.value)}
                        sx={{ borderRadius: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setUpdateDialogOpen(false)}
                variant="outlined"
                sx={{ borderRadius: 1 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitUpdate}
                variant="contained"
                disabled={!statusUpdate}
                sx={{
                  borderRadius: 1,
                  bgcolor: "text.primary",
                  "&:hover": { bgcolor: "text.secondary" },
                }}
              >
                Submit Update
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </IonContent>
    </IonPage>
  )
}